import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  DialogTitle,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import {
  normalizeUnit,
  needsSameUnitReset,
  orderQuantity,
} from "./takeoffQuantity";
import { MatchRow } from "./TakeoffMatchRow";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../lib/api";
import {
  type CatalogVariation,
  type Distributor,
} from "../../lib/distributorsApi";

export type ProductMatch = {
  key: string;
  name: string;
  sku: string;
  unit: string;
  price?: string | null;
  is_dimensional?: boolean;
  dimension_options?: CatalogVariation[];
};
export type OrderReviewRow = {
  id: string;
  material: string;
  quantity: string;
  unit: string;
  notes: string;
  source: string;
  product: ProductMatch | null;
  factor: string;
  coverage_quantity?: string;
  confirmed: boolean;
  excluded: boolean;
  exclusion_reason: string;
  conversion_source?: string;
  candidates?: ProductMatch[];
  search_hint?: string;
  length_value?: string | null;
  length_uom?: string;
};
type Scope = {
  requirements: string[];
  scope: string[];
  comments: string;
  proposal_number: string;
  warning?: string;
  order_verification?: { service: string; item: string }[];
};
type Context = {
  job: number | null;
  job_label: string;
  opportunity: number | null;
  opportunity_label: string;
  measurement: number | null;
  project_notes: string;
  proposals: { id: number; number: string }[];
  accepted_scope?: Scope;
  scope_confirmed?: boolean;
  ordering_details_verified?: boolean;
  live_catalog?: boolean;
  existing_drafts?: {
    id: number;
    distributor: string;
    source_revision: number;
    order: number | null;
    order_number: string;
    status: string;
  }[];
};
export type TakeoffOrderReview = {
  id: number;
  revision: number;
  source_revision: number;
  coverage_ratios_supported?: boolean;
  distributor: number;
  order: number | null;
  order_number?: string;
  order_status?: string;
  rows: OrderReviewRow[];
  context: Context;
};
type Suggestion = { material: string; reason: string; source: string };
const base = "/api/distributors/takeoff-orders/";
const errorText = (error: unknown) => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  return typeof data === "object" && data && "detail" in data
    ? String(data.detail)
    : data
      ? JSON.stringify(data)
      : "Could not complete this step. Your edits are still here; please retry.";
};

export function TakeoffOrderDialog({
  measurementId,
  materialType,
  sourceRevision,
  proposalId,
  onClose,
}: {
  measurementId?: number;
  materialType?: string;
  sourceRevision?: number;
  proposalId?: number;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [context, setContext] = useState<Context | null>(null);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [distributor, setDistributor] = useState<number | null>(null);
  const [proposal, setProposal] = useState<number | null>(proposalId || null);
  const [review, setReview] = useState<TakeoffOrderReview | null>(null);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [error, setError] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[] | null>(null);
  const [dirty, setDirty] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [newMaterial, setNewMaterial] = useState("");
  const [newUnit, setNewUnit] = useState("EA");
  const openOrder = (id: number) =>
    navigate(`/operations/distributors?tab=create&draft=${id}`);
  const close = () => {
    if (busy || aiBusy) return;
    if (!dirty || window.confirm("Close without saving these review edits?"))
      onClose();
  };
  useEffect(() => {
    let current = true;
    setError("");
    Promise.all([
      apiClient.get<Context>(
        `${base}context/?${proposalId ? `proposal=${proposalId}` : `measurement=${measurementId}`}`,
      ),
      apiClient.getAllPages<Distributor>(
        "/api/distributors/distributors/?is_active=true",
      ),
    ])
      .then(([c, d]) => {
        if (!current) return;
        setContext(c.data);
        setDistributors(d.data);
        if (c.data.proposals.length === 1) setProposal(c.data.proposals[0].id);
      })
      .catch((e) => current && setError(errorText(e)));
    return () => {
      current = false;
    };
  }, [measurementId, proposalId, loadAttempt]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const prepare = () =>
    run(async () => {
      const result = (
        await apiClient.post<TakeoffOrderReview>(base, {
          detailed_measurement: measurementId,
          material_type: materialType,
          source_revision: sourceRevision,
          distributor,
          proposal,
        })
      ).data;
      setReview(result);
      setDirty(false);
    });
  const save = async () => {
    if (!review) throw new Error("No review");
    if (!dirty) return review;
    const result = (
      await apiClient.patch<TakeoffOrderReview>(`${base}${review.id}/`, {
        revision: review.revision,
        rows: review.rows,
        scope_confirmed: !!review.context.scope_confirmed,
        ordering_details_verified: !!review.context.ordering_details_verified,
      })
    ).data;
    setReview(result);
    setDirty(false);
    return result;
  };
  const askAI = async () => {
    setAiBusy(true);
    setError("");
    try {
      const saved = await save();
      setSuggestions(
        (
          await apiClient.post<{ suggestions: Suggestion[] }>(
            `${base}${saved.id}/ai_review/`,
            {},
          )
        ).data.suggestions,
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setAiBusy(false);
    }
  };
  const add = (material: string, source = "Manual addition") => {
    if (!review || !material.trim()) return;
    setReview({
      ...review,
      rows: [
        ...review.rows,
        {
          id: crypto.randomUUID(),
          material: material.slice(0, 120),
          quantity: "1",
          unit: newUnit,
          notes: "",
          source,
          product: null,
          factor: "",
          confirmed: false,
          excluded: false,
          exclusion_reason: "",
        },
      ],
    });
    setDirty(true);
    setNewMaterial("");
  };
  const scope = review?.context.accepted_scope;
  const needsReview =
    review?.rows.filter(
      (r) =>
        !r.excluded &&
        (!r.confirmed ||
          !r.product ||
          orderQuantity(r.quantity, r.factor, r.coverage_quantity) === null ||
          needsSameUnitReset(
            r.unit,
            r.product.unit,
            r.factor,
            r.coverage_quantity,
          )),
    ).length || 0;
  return (
    <Modal open onClose={close}>
      <ModalDialog
        sx={{
          width: "min(1100px, 96vw)",
          maxHeight: "92dvh",
          p: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: 2,
            pr: 6,
            flexShrink: 0,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <ModalClose
            aria-label="Close materials review"
            disabled={busy || aiBusy}
          />
          <DialogTitle>Create Materials Order</DialogTitle>
          <Typography level="body-sm">
            {review
              ? "2. Confirm products → 3. Review your order"
              : "1. Choose your distributor and proposal"}
          </Typography>
        </Box>
        <Box sx={{ overflowY: "auto", minHeight: 0, flex: "1 1 auto" }}>
          <Stack
            component="fieldset"
            disabled={busy || aiBusy}
            spacing={2}
            sx={{
              m: 0,
              border: 0,
              p: 2,

              minWidth: 0,
            }}
          >
            {error && (
              <Alert color="danger">
                {error}
                {!context && (
                  <Button onClick={() => setLoadAttempt((v) => v + 1)}>
                    Retry
                  </Button>
                )}
              </Alert>
            )}
            {!context && !error && <CircularProgress />}
            {!review && context && (
              <>
                <Typography level="title-md">
                  {context.job_label ||
                    context.opportunity_label ||
                    "Unlinked measurement"}{" "}
                  · Takeoff revision {sourceRevision}
                </Typography>
                {!!context.existing_drafts?.length && (
                  <Alert color="warning">
                    <Stack spacing={1}>
                      <Typography level="title-sm">
                        Orders already started from this measurement
                      </Typography>
                      {context.existing_drafts.map((d) => (
                        <Button
                          key={d.id}
                          variant="plain"
                          onClick={() =>
                            run(async () => {
                              setReview(
                                (
                                  await apiClient.get<TakeoffOrderReview>(
                                    `${base}${d.id}/`,
                                  )
                                ).data,
                              );
                            })
                          }
                        >
                          {d.distributor} · {d.order_number || "Saved review"} ·{" "}
                          {d.status} · revision {d.source_revision}
                        </Button>
                      ))}
                    </Stack>
                  </Alert>
                )}
                <FormControl>
                  <FormLabel>Distributor</FormLabel>
                  <Select
                    placeholder="Choose your distributor"
                    value={distributor}
                    onChange={(_, v) => setDistributor(v)}
                  >
                    {distributors.map((d) => (
                      <Option key={d.id} value={d.id}>
                        {d.name}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Accepted proposal</FormLabel>
                  {context.proposals.length ? (
                    <Select
                      placeholder="Select the accepted scope for this order"
                      value={proposal}
                      onChange={(_, v) => setProposal(v)}
                    >
                      {context.proposals.map((p) => (
                        <Option key={p.id} value={p.id}>
                          {p.number}
                        </Option>
                      ))}
                    </Select>
                  ) : (
                    <Typography level="body-sm">
                      No accepted proposal found. You can select products
                      manually.
                    </Typography>
                  )}
                </FormControl>
                <Alert color="primary">
                  We’ll use the saved takeoff quantities and accepted product
                  selections to prepare a draft. You’ll review it before
                  anything is sent.
                </Alert>
              </>
            )}
            {review && (
              <>
                {review.order ? (
                  <Alert color="success">
                    <Stack>
                      <Typography>
                        This review already created {review.order_number}.
                      </Typography>
                      <Button
                        onClick={() =>
                          review.order_status && review.order_status !== "draft"
                            ? navigate("/operations/distributors?tab=history")
                            : openOrder(review.order!)
                        }
                      >
                        Open existing order
                      </Button>
                    </Stack>
                  </Alert>
                ) : (
                  <>
                    <Card variant="soft">
                      <Typography level="title-md">
                        {scope?.proposal_number
                          ? `Accepted scope · ${scope.proposal_number}`
                          : "Project requirements"}
                      </Typography>
                      {scope?.warning && (
                        <Alert color="warning">{scope.warning}</Alert>
                      )}
                      {scope?.requirements.map((r, i) => (
                        <Typography key={i} level="title-sm">
                          {r}
                        </Typography>
                      ))}
                      <details>
                        <summary>Scope, upgrades and project notes</summary>
                        {scope?.scope.map((s, i) => (
                          <Typography key={i} level="body-sm">
                            {s}
                          </Typography>
                        ))}
                        <Typography
                          sx={{ whiteSpace: "pre-wrap" }}
                          level="body-sm"
                        >
                          {scope?.comments}
                          {"\n"}
                          {review.context.project_notes}
                        </Typography>
                      </details>
                      <Checkbox
                        label="I reviewed the accepted selections, upgrades and project requirements"
                        checked={!!review.context.scope_confirmed}
                        onChange={(e) => {
                          setReview({
                            ...review,
                            context: {
                              ...review.context,
                              scope_confirmed: e.target.checked,
                            },
                          });
                          setDirty(true);
                        }}
                      />
                      {!!scope?.order_verification?.length && (
                        <Alert color="warning">
                          <Stack spacing={1}>
                            <Typography>
                              Final ordering review required:{" "}
                              {scope.order_verification
                                .map((item) => `${item.service}: ${item.item}`)
                                .join(", ")}
                            </Typography>
                            <Checkbox
                              label="I verified final dimensions, layout, installation method and products for ordering"
                              checked={
                                !!review.context.ordering_details_verified
                              }
                              onChange={(e) => {
                                setReview({
                                  ...review,
                                  context: {
                                    ...review.context,
                                    ordering_details_verified: e.target.checked,
                                  },
                                });
                                setDirty(true);
                              }}
                            />
                          </Stack>
                        </Alert>
                      )}
                    </Card>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      gap={1}
                      alignItems="center"
                    >
                      <Button
                        variant="outlined"
                        loading={aiBusy}
                        disabled={busy}
                        onClick={askAI}
                      >
                        Review materials with AI
                      </Button>
                      <Typography level="body-xs">
                        Checks scope and notes for missing materials.
                        Suggestions never change your order automatically.
                      </Typography>
                    </Stack>
                    {suggestions && (
                      <Card variant="soft">
                        <Typography level="title-md">
                          AI suggestions to review
                        </Typography>
                        {!suggestions.length && (
                          <Typography>
                            No additional materials suggested.
                          </Typography>
                        )}
                        {suggestions.map((s, i) => (
                          <Box key={i} sx={{ py: 1 }}>
                            <Typography level="title-sm">
                              {s.material}
                            </Typography>
                            <Typography level="body-sm">{s.reason}</Typography>
                            <Typography level="body-xs">
                              Source: {s.source}
                            </Typography>
                            <Button
                              size="sm"
                              variant="outlined"
                              onClick={() => {
                                add(
                                  s.material,
                                  `AI suggestion — verify quantity: ${s.source}`.slice(
                                    0,
                                    1000,
                                  ),
                                );
                                setSuggestions(
                                  suggestions.filter((_, j) => j !== i),
                                );
                              }}
                            >
                              Add for review
                            </Button>
                            <Button
                              size="sm"
                              variant="plain"
                              color="neutral"
                              onClick={() =>
                                setSuggestions(
                                  suggestions.filter((_, j) => j !== i),
                                )
                              }
                            >
                              Dismiss
                            </Button>
                          </Box>
                        ))}
                      </Card>
                    )}
                    {review.rows.map((row) => (
                      <MatchRow
                        key={row.id}
                        row={row}
                        reviewId={review.id}
                        allowConversions={!!review.coverage_ratios_supported}
                        onChange={(next) => {
                          setReview({
                            ...review,
                            rows: review.rows.map((r) =>
                              r.id === next.id ? next : r,
                            ),
                          });
                          setDirty(true);
                        }}
                      />
                    ))}
                    <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
                      <Input
                        aria-label="Additional material"
                        placeholder="Add another material"
                        value={newMaterial}
                        onChange={(e) => setNewMaterial(e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <Input
                        aria-label="Additional material unit"
                        placeholder="Unit"
                        value={newUnit}
                        onChange={(e) => setNewUnit(e.target.value)}
                        sx={{ maxWidth: 110 }}
                      />
                      <Button
                        variant="outlined"
                        disabled={!newMaterial.trim() || !newUnit.trim()}
                        onClick={() => add(newMaterial)}
                      >
                        Add item
                      </Button>
                    </Stack>
                    <Typography level="body-xs">
                      {needsReview
                        ? `${needsReview} ${needsReview === 1 ? "item needs" : "items need"} confirmation`
                        : "All included products confirmed"}
                      . Product units are rounded up; review pack coverage
                      before ordering.
                    </Typography>
                  </>
                )}
              </>
            )}
          </Stack>
        </Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          gap={1}
          sx={{
            flexWrap: "wrap",
            flexShrink: 0,
            zIndex: 1,
            p: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.surface",
          }}
        >
          <Button
            variant="plain"
            color="neutral"
            disabled={busy || aiBusy}
            onClick={close}
          >
            Close
          </Button>
          {!review ? (
            <Button
              loading={busy}
              disabled={
                !distributor || (!!context?.proposals.length && !proposal)
              }
              onClick={prepare}
            >
              Prepare materials
            </Button>
          ) : (
            !review.order && (
              <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
                <Button
                  variant="outlined"
                  loading={busy}
                  disabled={aiBusy || !dirty}
                  onClick={() =>
                    run(async () => {
                      await save();
                    })
                  }
                >
                  Save review
                </Button>
                <Button
                  loading={busy}
                  disabled={
                    aiBusy ||
                    !!needsReview ||
                    !review.context.scope_confirmed ||
                    (!!scope?.order_verification?.length &&
                      !review.context.ordering_details_verified) ||
                    (!review.coverage_ratios_supported &&
                      review.rows.some(
                        (row) =>
                          !row.excluded &&
                          row.product &&
                          normalizeUnit(row.unit) !==
                            normalizeUnit(row.product.unit),
                      ))
                  }
                  onClick={() =>
                    run(async () => {
                      const saved = await save();
                      const result = (
                        await apiClient.post<{ order: number }>(
                          `${base}${saved.id}/complete/`,
                          { revision: saved.revision },
                        )
                      ).data;
                      openOrder(result.order);
                    })
                  }
                >
                  Continue to Create Order
                </Button>
              </Stack>
            )
          )}
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
